import React, { useState } from 'react';
import { US_STATES } from '../../data/usStates';

// §authenticated-patient handoff (PRD 2026-08-11 §4.4): a patient who arrived from
// the dashboard already has an address on file, so they CONFIRM it rather than
// retype it. useHandoffSeed seeds this step's answer with `fromHandoff: true`; when
// that seed is complete we render a confirm card instead of the empty form.
// "Use a different address" drops straight back to the normal (prefilled) form, and
// the confirmed answer is emitted in the same shape as a typed one.
const isConfirmable = (v) => !!(v && v.fromHandoff && v.address1 && v.city && v.state && v.zipCode);

const ShippingAddressStep = ({ question, value, onAnswer, onNext }) => {
  const [address1, setAddress1] = useState(value?.address1 || '');
  const [address2, setAddress2] = useState(value?.address2 || '');
  const [city, setCity] = useState(value?.city || '');
  const [state, setState] = useState(value?.state || '');
  const [zipCode, setZipCode] = useState(value?.zipCode || '');
  const [errors, setErrors] = useState({});
  // Derived (not initial-state) so a seed that lands after this step mounts still
  // shows the confirm card; "Use a different address" latches the form open.
  const [editing, setEditing] = useState(false);
  const confirming = isConfirmable(value) && !editing;

  const validateForm = () => {
    const newErrors = {};

    if (!address1.trim()) {
      newErrors.address1 = 'Address is required';
    }
    if (!city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!state) {
      newErrors.state = 'State is required';
    }
    if (!zipCode.trim()) {
      newErrors.zipCode = 'ZIP Code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(zipCode.trim())) {
      newErrors.zipCode = 'Please enter a valid ZIP Code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    onAnswer({
      address1: address1.trim(),
      address2: address2.trim(),
      city: city.trim(),
      state,
      zipCode: zipCode.trim(),
    });
    onNext();
  };

  // Emit the seeded address as-is (minus the internal handoff flag) and move on.
  const handleConfirm = () => {
    onAnswer({
      address1: (value.address1 || '').trim(),
      address2: (value.address2 || '').trim(),
      city: (value.city || '').trim(),
      state: value.state,
      zipCode: (value.zipCode || '').trim(),
    });
    onNext();
  };

  if (confirming) {
    return (
      <div data-testid={`step-${question.id}`}>
        <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
          Ship to this address?
        </h2>
        <p className="text-center text-gray-500 mb-8">
          This is the address we have on file for you. If prescribed, we'll ship here.
        </p>

        <div className="max-w-md mx-auto">
          <div
            className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-900"
            data-testid="address-confirm-card"
          >
            <p>{value.address1}</p>
            {value.address2 ? <p>{value.address2}</p> : null}
            <p>{`${value.city}, ${value.state} ${value.zipCode}`}</p>
          </div>

          <button
            type="button"
            onClick={handleConfirm}
            className="btn-primary mt-6"
            data-testid="confirm-address-button"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="w-full mt-3 text-sm text-gray-500 underline"
            data-testid="edit-address-button"
          >
            Use a different address
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`step-${question.id}`}>
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        Shipping Address
      </h2>
      <p className="text-center text-gray-500 mb-8">
        If prescribed, we need your current home address on file.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Address 1 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address 1 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={address1}
            onChange={(e) => { setAddress1(e.target.value); setErrors({ ...errors, address1: '' }); }}
            placeholder="Enter your street address"
            className={`form-input ${errors.address1 ? 'border-red-500' : ''}`}
            data-testid="address1-input"
          />
          {errors.address1 && (
            <p className="text-red-500 text-xs mt-1">{errors.address1}</p>
          )}
        </div>

        {/* Address 2 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address 2 <span className="text-gray-400">(Optional)</span>
          </label>
          <input
            type="text"
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
            placeholder="Apartment, suite, unit, etc."
            className="form-input"
            data-testid="address2-input"
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => { setCity(e.target.value); setErrors({ ...errors, city: '' }); }}
            placeholder="City"
            className={`form-input ${errors.city ? 'border-red-500' : ''}`}
            data-testid="city-input"
          />
          {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
        </div>

        {/* State and ZIP */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State <span className="text-red-500">*</span>
            </label>
            <select
              value={state}
              onChange={(e) => { setState(e.target.value); setErrors({ ...errors, state: '' }); }}
              className={`form-input ${errors.state ? 'border-red-500' : ''}`}
              data-testid="state-select"
            >
              {US_STATES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => { setZipCode(e.target.value); setErrors({ ...errors, zipCode: '' }); }}
              placeholder="12345"
              maxLength={10}
              className={`form-input ${errors.zipCode ? 'border-red-500' : ''}`}
              data-testid="zipcode-input"
            />
            {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
          </div>
        </div>

        <button type="submit" className="btn-primary mt-6" data-testid="continue-button">
          Continue
        </button>
      </form>
    </div>
  );
};

export default ShippingAddressStep;
